import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useEditEventNotification } from "../../hooks/useEditEventNotification";
import EventNotificationEmailEditor from "../EventNotificationEmailEditor/EventNotificationEmailEditor";
import EmailTemplateVariableSelector from "@/modules/emailManagement/features/EmailTemplateManagement/components/EmailTemplateVariableSelector/EmailTemplateVariableSelector";

const EditEventNotification: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    form,
    submitForm,
    fetchNotification,
    handleCancel,
    notification,
    isLoading,
  } = useEditEventNotification();

  const { control, handleSubmit, formState, watch, getValues, setValue } = form;
  const { isSubmitting } = formState;

  const isMailChannelEnabled = watch("isMailChannelEnabled");
  const isSmsChannelEnabled = watch("isSmsChannelEnabled");

  useEffect(() => {
    if (id) {
      fetchNotification(Number(id));
    }
  }, [id, fetchNotification]);

  const handleInsertVariableToSmsContent = (variableName: string) => {
    const textarea = document.querySelector(
      "textarea[name='smsContent']",
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const currentContent = getValues("smsContent");
    const { selectionStart, selectionEnd } = textarea;
    const newContent =
      currentContent.slice(0, selectionStart) +
      variableName +
      currentContent.slice(selectionEnd);

    setValue("smsContent", newContent);

    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd =
        selectionStart + variableName.length;
      textarea.focus();
    });
  };

  return (
    <MainPageWrapper loading={isLoading} title="">
      <div className="max-w-4xl mx-auto p-4">
        <Form {...form}>
          <form
            className="space-y-8 pb-24 bg-white"
            onSubmit={handleSubmit(submitForm)}
          >
            <fieldset>
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">
                    Edit Notification: {notification?.name}
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Configure the notification channels and templates
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Email Settings</h3>

                    <FormField
                      control={control}
                      name="isMailChannelEnabled"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">
                              Email Channel
                            </FormLabel>
                            <p className="text-sm text-gray-500">
                              Enable or disable email notifications
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              checked={Boolean(field.value)}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {isMailChannelEnabled && (
                      <EventNotificationEmailEditor form={form} />
                    )}
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">SMS Settings</h3>

                    <FormField
                      control={control}
                      name="isSmsChannelEnabled"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">
                              SMS Channel
                            </FormLabel>
                            <p className="text-sm text-gray-500">
                              Enable or disable SMS notifications
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              checked={Boolean(field.value)}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {isSmsChannelEnabled && (
                      <FormField
                        control={control}
                        name="smsContent"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex justify-between items-center">
                              <FormLabel>SMS Content</FormLabel>
                              <EmailTemplateVariableSelector
                                onVariableSelect={
                                  handleInsertVariableToSmsContent
                                }
                              />
                            </div>
                            <FormControl>
                              <Textarea
                                {...field}
                                className="w-full min-h-[100px] h-auto"
                                placeholder="Enter SMS message content..."
                              />
                            </FormControl>
                            <p className="text-sm text-gray-500 mt-1">
                              Plain text message for SMS notifications
                            </p>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t bg-white mt-8">
                    <Button
                      onClick={handleCancel}
                      type="button"
                      variant="outline"
                    >
                      Cancel
                    </Button>
                    <Button disabled={isLoading} type="submit">
                      {isSubmitting || isLoading
                        ? "Saving..."
                        : "Save Notification"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </fieldset>
          </form>
        </Form>
      </div>
    </MainPageWrapper>
  );
};

export default EditEventNotification;
